import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import GreenButton from '../Components/Buttons/Greenbutton';
import { useCart } from '../context/CartContext';
import { getProduct } from '../lib/api';
import { formatNpr } from '../lib/currency';
import { getOptimizedImageUrl } from '../lib/imageUrl';
import { getProductDisplayId } from '../lib/productDisplayId';

function ProductDetailsPage() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [zoomPosition, setZoomPosition] = useState({ x: 50, y: 50 });
  const [isZoomActive, setIsZoomActive] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const { addItem } = useCart();

  useEffect(() => {
    async function fetchProduct() {
      try {
        const nextProduct = await getProduct(productId);
        setProduct(nextProduct);
        setActiveImage((nextProduct.images && nextProduct.images[0]) || nextProduct.image);
      } catch (fetchError) {
        setError(fetchError.message);
      } finally {
        setIsLoading(false);
      }
    }

    fetchProduct();
  }, [productId]);

  async function handleAddToCart() {
    if (!product) {
      return;
    }

    setIsAdding(true);

    try {
      await addItem(product.id, 1);
    } catch (_error) {
      // CartContext surfaces the real error message.
    } finally {
      window.setTimeout(() => {
        setIsAdding(false);
      }, 700);
    }
  }

  function handleImageMouseMove(event) {
    const bounds = event.currentTarget.getBoundingClientRect();
    const x = ((event.clientX - bounds.left) / bounds.width) * 100;
    const y = ((event.clientY - bounds.top) / bounds.height) * 100;

    setZoomPosition({
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    });
  }

  return (
    <section className="page-card">
      <h1 className="page-card__title">Product Details</h1>
      {isLoading && <p className="products-state">Loading product details...</p>}
      {error && <p className="products-state products-state--error">{error}</p>}
      {!isLoading && !error && product && (
        <article className="product-details">
          <div className="product-details__media">
            <div
              className={`product-details__image-viewer ${isZoomActive ? 'is-zooming' : ''}`}
              onMouseEnter={() => {
                setIsZoomActive(true);
              }}
              onMouseLeave={() => {
                setIsZoomActive(false);
              }}
              onMouseMove={handleImageMouseMove}
            >
              <img
                className="product-details__image"
                src={getOptimizedImageUrl(activeImage || product.image, 1000)}
                alt={product.title}
                decoding="async"
              />
              <div
                className="product-details__image-focus"
                style={{
                  backgroundImage: `url(${getOptimizedImageUrl(activeImage || product.image, 1600)})`,
                  backgroundPosition: `${zoomPosition.x}% ${zoomPosition.y}%`,
                }}
              />
            </div>
            <div className="product-details__badge">Top Rated</div>
            {product.images?.length ? (
              <div className="product-details__thumbnails">
                {product.images.map((image, index) => (
                  <button
                    key={`${product.id}-image-${index + 1}`}
                    type="button"
                    className={`product-details__thumbnail ${image === (activeImage || product.image) ? 'is-active' : ''}`}
                    onClick={() => {
                      setActiveImage(image);
                    }}
                  >
                    <img
                      src={getOptimizedImageUrl(image, 200)}
                      alt={`${product.title} view ${index + 1}`}
                      loading="lazy"
                      decoding="async"
                    />
                  </button>
                ))}
              </div>
            ) : null}
          </div>
          <div className="product-details__content">
            <p className="product-card__category">{product.category}</p>
            <h2 className="product-details__title">{product.name}</h2>
            <div className="product-details__meta">
              <span className="product-details__meta-item">
                Product ID: {getProductDisplayId(product.id)}
              </span>
              <span className="product-details__meta-item">
                {product.rating?.count ?? 0} reviews
              </span>
              <span className="product-details__meta-item">{product.stock} in stock</span>
              <span className="product-details__meta-item">Secure checkout</span>
            </div>
            <p className="product-details__price">{formatNpr(product.price)}</p>
            <p className="product-details__description">{product.description}</p>
            <div className="product-details__footer">
              <p className="product-details__rating">
                Rating: {product.rating?.rate ?? 'N/A'} / 5
              </p>
              <div className="product-details__actions">
                <GreenButton
                  text={isAdding ? 'Added' : 'Add to Cart'}
                  onClick={() => {
                    void handleAddToCart();
                  }}
                />
                <Link to="/products" className="product-card__link">
                  Back to Products
                </Link>
              </div>
            </div>
          </div>
        </article>
      )}
    </section>
  );
}

export default ProductDetailsPage;
